/**
 * CategoryNotesSection — one note category, rendered as its own member-profile
 * section (TRUS-420).
 *
 * Generalises what was `RecoveryPlanSection`. The ticket left open "whether
 * other tags (Rebuild, Referrals, and so on) should later surface in their own
 * sections the way Recovery Plan does"; the answer is yes, so a section is no
 * longer a Recovery-Plan special case — it is any category an admin has flagged
 * `show_as_section`.
 *
 * It READS the notes tagged with the category rather than copying them, which is
 * what makes the ticket's sync requirements structural: editing a note in the
 * Activity feed shows the change here because it is the same row, and clearing
 * the tag drops it out of here because the filter stops matching.
 *
 * Reads through the activity feed endpoint (`category_id` + `origin=note`)
 * rather than a per-category endpoint of its own — one read path, already
 * tested, instead of a second one returning the same rows.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { ClipboardCheck, Loader2, Tag, User } from 'lucide-react';

import {
  memberActivityFeedAPI,
  type ActivityFeedItem,
  type NoteCategory,
} from '@/services/api/memberActivityFeed';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { getApiErrorMessage } from '@/utils/apiErrors';

interface CategoryNotesSectionProps {
  memberId: number;
  /** The category this section is for. Its own `name` is the heading. */
  category: NoteCategory;
}

export default function CategoryNotesSection({ memberId, category }: CategoryNotesSectionProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();

  // All state above the callbacks that read it (the TRUS-418 ordering rule).
  const [notes, setNotes] = useState<ActivityFeedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await memberActivityFeedAPI.getFeed(memberId, {
        categoryId: category.id,
        origin: 'note',
        limit: 200,
      });
      setNotes(res.items || []);
    } catch (e: any) {
      setError(getApiErrorMessage(e) || t('categorySection.loadFailed', 'Could not load this section.'));
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [memberId, category.id, t]);

  useEffect(() => {
    load();
  }, [load]);

  const accent = category.color || undefined;

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2">
        <ClipboardCheck className="w-5 h-5 mt-0.5 shrink-0" style={{ color: accent }} />
        <div>
          <h3 className="text-lg font-semibold">{category.name}</h3>
          <p className="text-sm text-gray-500 max-w-2xl">
            {t(
              'categorySection.subtitle',
              'Notes tagged with this category in the Activity feed appear here automatically. Edit a note in Activity and the change shows here; remove the tag and it drops off.'
            )}
          </p>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/30 dark:border-red-900 dark:text-red-300">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-10 text-gray-400">
          <Loader2 className="w-4 h-4 animate-spin" />
          {t('categorySection.loading', 'Loading…')}
        </div>
      ) : notes.length === 0 ? (
        <div className="rounded-lg border border-dashed border-v2border py-10 text-center text-gray-400">
          <p className="text-sm">{t('categorySection.empty', 'Nothing tagged here yet.')}</p>
          <p className="text-xs mt-1">
            {t(
              'categorySection.emptyHint',
              'Tag a note with this category in the Activities tab and it will appear here.'
            )}
          </p>
        </div>
      ) : (
        <ol className="space-y-2">
          {notes.map((n) => (
            <li
              key={n.id}
              className="relative rounded-lg border border-v2border bg-surface pl-4 pr-3 py-3"
            >
              <span
                className="absolute left-0 top-0 bottom-0 w-1 rounded-l-lg"
                style={{ backgroundColor: accent || 'var(--v2accent, #0D6B66)' }}
                aria-hidden="true"
              />
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                {n.title && <span className="text-sm font-medium">{n.title}</span>}
                <span className="text-xs text-gray-400 inline-flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {n.author_name || t('categorySection.unknownAuthor', 'Unknown')}
                </span>
                <span className="text-xs text-gray-400">
                  {n.occurred_at ? fmt.dateTime(n.occurred_at) : ''}
                </span>
                {n.meta?.edited && (
                  <span className="text-xs text-gray-400 italic">
                    {t('categorySection.edited', 'edited')}
                  </span>
                )}
              </div>

              {/* Full text, wrapped — the point of TRUS-420. */}
              {n.body && (
                <p className="mt-1.5 text-sm text-ink whitespace-pre-wrap leading-relaxed break-words">
                  {n.body}
                </p>
              )}

              {/* A note can carry several categories; show the others so the
                  reader knows this note also lives in another section. */}
              {n.categories?.length > 1 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {n.categories
                    .filter((c) => c.id !== category.id)
                    .map((c) => (
                      <span
                        key={c.id}
                        className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium border"
                        style={{
                          color: c.color || undefined,
                          borderColor: c.color ? `${c.color}55` : undefined,
                          backgroundColor: c.color ? `${c.color}14` : undefined,
                        }}
                      >
                        <Tag className="w-2.5 h-2.5" />
                        {c.name}
                      </span>
                    ))}
                </div>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
